import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchSkillsCoverage } from '../api/skills'
import { assignSkillsToAgent } from '../api/team'
import SkillPill from './SkillPill'
import Icon from './Icon'
import styles from './AssignAgentSkillModal.module.css'

// Skill classifications, in the backend _VALID_SKILL_TYPES order used elsewhere.
const TYPE_ORDER = ['Core', 'Technical', 'Process', 'General']

// SkillPill colour variant from the classification (mirrors AgentSkillsModal).
function pillVariant(type) {
  return (type || 'general').toLowerCase()
}

/**
 * AssignAgentSkillModal — assign one or more skills to a single team member, opened
 * from the agent Details modal's "Assign Skill" button. The mirror image of
 * AssignSkillModal (which assigns one skill to many members): here the member is
 * fixed and the list is the set of skills. Skills the member already holds are shown
 * pre-checked and disabled (they can't be re-assigned); every other skill is a toggle.
 *
 * Props:
 *   agentId          {number|string} — the member being assigned skills
 *   agentName        {string}        — display name (header)
 *   existingSkillIds {Array<number>} — skill ids the member already holds
 *   onClose    {function} — dismiss the modal
 *   onAssigned {function} — called after a successful save so the parent can refresh
 */
export default function AssignAgentSkillModal({
  agentId, agentName, existingSkillIds = [], onClose, onAssigned,
}) {
  const [skills, setSkills] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')

  // Newly-selected skill ids (excludes those the member already holds).
  const [selected, setSelected] = useState(() => new Set())
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Stable set of the already-held ids for O(1) membership checks.
  const existing = useMemo(() => new Set(existingSkillIds), [existingSkillIds])

  const didFetch = useRef(false)
  useEffect(() => {
    if (didFetch.current) return
    didFetch.current = true
    fetchSkillsCoverage()
      .then(data => setSkills(data.skills ?? []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  function toggle(id) {
    if (existing.has(id)) return  // already held — non-toggleable
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Distinct types present, for the dropdown, in _VALID_SKILL_TYPES order.
  const types = useMemo(() => {
    const set = new Set(skills.map(s => s.type).filter(Boolean))
    return TYPE_ORDER.filter(t => set.has(t))
  }, [skills])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return skills.filter(s => {
      // Prefix-match each name token, mirroring the Team page / AssignSkillModal search.
      const matchesSearch =
        term === '' ||
        s.name.toLowerCase().split(/\s+/).some(part => part.startsWith(term))
      const matchesType = typeFilter === 'all' || s.type === typeFilter
      return matchesSearch && matchesType
    })
  }, [skills, search, typeFilter])

  async function handleSave() {
    const ids = [...selected]
    if (ids.length === 0 || saving) return
    setSaving(true)
    setSaveError('')
    try {
      // Assigning a skill that had a pending AI suggestion promotes that proposal; the
      // response's `promoted` count lets the parent drop the member's "new skills"
      // badge by the same amount.
      const res = await assignSkillsToAgent(agentId, ids)
      onAssigned?.(res?.promoted ?? 0)
      onClose()
    } catch (e) {
      setSaveError(e.message)
      setSaving(false)
    }
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  const count = selected.size

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Assign skill to member">

        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <Icon name="close" />
        </button>

        {/* ── Header ──────────────────────────────────── */}
        <div className={styles.header}>
          <span className={styles.headerIcon}><Icon name="members" /></span>
          <h2 className={styles.title}>
            Assign <span className={styles.memberName}>{agentName}</span> to a Skill
          </h2>
        </div>

        {/* ── List header — title + search + type filter ── */}
        <div className={styles.listHeader}>
          <div className={styles.listTitleRow}>
            <span className={styles.listIcon}><Icon name="skills" /></span>
            <span className={styles.listTitle}>Skills</span>
          </div>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}><Icon name="search" /></span>
            <input
              className={styles.search}
              type="text"
              placeholder="Search for skill"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && setSearch('')}
            />
            {search && (
              <button type="button" className={styles.clearSearch} onClick={() => setSearch('')}>
                Clear
              </button>
            )}
          </div>
          <select
            className={styles.filterSelect}
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* ── Skill list — independently scrollable ───── */}
        <div className={styles.list}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading skills…</p>
            </div>
          ) : error ? (
            <div className={styles.errorState}>Failed to load: {error}</div>
          ) : filtered.length === 0 ? (
            <div className={styles.empty}>No skills match your search.</div>
          ) : filtered.map(s => {
            const already = existing.has(s.id)
            const checked = already || selected.has(s.id)
            return (
              <div
                key={s.id}
                className={`${styles.row} ${checked ? styles.rowSelected : ''} ${already ? styles.rowDisabled : ''}`}
                onClick={() => toggle(s.id)}
                role="checkbox"
                aria-checked={checked}
                aria-disabled={already}
                tabIndex={already ? -1 : 0}
                onKeyDown={e => {
                  if (!already && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(s.id) }
                }}
                title={already ? 'Already has this skill' : undefined}
              >
                <span className={`${styles.checkbox} ${checked ? styles.checkboxOn : ''}`}>
                  {checked && <Icon name="check" />}
                </span>

                <span className={styles.skillName}>{s.name}</span>

                <SkillPill label={s.type} variant={pillVariant(s.type)} />

                {already && <span className={styles.alreadyTag}>Assigned</span>}
              </div>
            )
          })}
        </div>

        {/* ── Footer ──────────────────────────────────── */}
        <div className={styles.footer}>
          {saveError && <span className={styles.saveError}>{saveError}</span>}
          <button
            className={`${styles.footerBtn} ${styles.footerPrimary}`}
            type="button"
            onClick={handleSave}
            disabled={count === 0 || saving}
          >
            {saving ? 'Saving…' : count === 0 ? 'Save' : `Assign ${count} skill${count !== 1 ? 's' : ''}`}
          </button>
          <button className={styles.footerBtn} type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>

      </div>
    </div>
  )
}
